/**
 * Curated storefront token catalog.
 *
 * Verified tokens are intended to map to the most trusted default picks.
 * Community tokens are still exposed, but they stay explicitly labeled so the
 * storefront does not blur the line between curated defaults and broader
 * liquidity-discoverable assets.
 */

export type TokenTier = 'project' | 'verified' | 'community';

export interface TokenInfo {
  mint: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUri: string;
  tier: TokenTier;
}

const VERIFIED_TOKENS: TokenInfo[] = [
  {
    mint: 'So11111111111111111111111111111111111111112',
    symbol: 'SOL',
    name: 'Solana',
    decimals: 9,
    logoUri: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
    tier: 'verified',
  },
  {
    mint: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
    symbol: 'JUP',
    name: 'Jupiter',
    decimals: 6,
    logoUri: 'https://static.jup.ag/jup/icon.png',
    tier: 'verified',
  },
  {
    mint: 'HZ1JovNiVvGrGNiiYvEozEVgZ58xaU3RKwX8eACQBCt3',
    symbol: 'PYTH',
    name: 'Pyth Network',
    decimals: 6,
    logoUri: 'https://pyth.network/token.svg',
    tier: 'verified',
  },
  {
    mint: 'rndrizKT3MK1iimdxRdWabcF7Zg7AR5T4nud4EkHBof',
    symbol: 'RENDER',
    name: 'Render Token',
    decimals: 8,
    logoUri: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/rndrizKT3MK1iimdxRdWabcF7Zg7AR5T4nud4EkHBof/logo.png',
    tier: 'verified',
  },
  {
    mint: 'hntyVP6YFm1Hg25TN9WGLqM12b8TQmcknKrdu1oxWux',
    symbol: 'HNT',
    name: 'Helium',
    decimals: 8,
    logoUri: 'https://shdw-drive.genesysgo.net/CYPATnXadVf2bVMwqMRs3Eo1rVRY5aSGAFfJi6KJPii2/hnt.png',
    tier: 'verified',
  },
];

const COMMUNITY_TOKENS: TokenInfo[] = [
  {
    mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
    symbol: 'BONK',
    name: 'Bonk',
    decimals: 5,
    logoUri: 'https://arweave.net/hQiPZOsRZXGXBJd_82PhVdlM_hACsT_q6wqwf5cSY7I',
    tier: 'community',
  },
  {
    mint: 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
    symbol: 'WIF',
    name: 'dogwifhat',
    decimals: 6,
    logoUri: 'https://bafkreibk3covs5ltyqxa272uodhculbr6kea6betiez7oz4nwnyg5yceni.ipfs.nftstorage.link/',
    tier: 'community',
  },
  {
    mint: 'jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL',
    symbol: 'JTO',
    name: 'Jito',
    decimals: 9,
    logoUri: 'https://metadata.jito.network/token/jto/image',
    tier: 'community',
  },
  {
    mint: '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R',
    symbol: 'RAY',
    name: 'Raydium',
    decimals: 6,
    logoUri: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R/logo.png',
    tier: 'community',
  },
  {
    mint: 'orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE',
    symbol: 'ORCA',
    name: 'Orca',
    decimals: 6,
    logoUri: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE/logo.png',
    tier: 'community',
  },
];

export const POPULAR_TOKENS: TokenInfo[] = [...VERIFIED_TOKENS, ...COMMUNITY_TOKENS];
export const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

function dedupeTokens(tokens: TokenInfo[]): TokenInfo[] {
  const seen = new Set<string>();
  const deduped: TokenInfo[] = [];

  for (const token of tokens) {
    if (seen.has(token.mint)) continue;
    seen.add(token.mint);
    deduped.push(token);
  }

  return deduped;
}

export function buildProjectToken(project: {
  token_mint: string;
  token_symbol: string;
  name: string;
  token_decimals: number;
  token_icon_url: string | null;
}): TokenInfo {
  return {
    mint: project.token_mint,
    symbol: project.token_symbol,
    name: project.name,
    decimals: project.token_decimals,
    logoUri: project.token_icon_url || '',
    tier: 'project',
  };
}

export function buildCuratedTokenList(projectToken?: TokenInfo): TokenInfo[] {
  return dedupeTokens(projectToken ? [projectToken, ...POPULAR_TOKENS] : POPULAR_TOKENS);
}

export function labelTokenTier(tier: TokenTier): string {
  switch (tier) {
    case 'project':
      return 'Project';
    case 'verified':
      return 'Verified';
    case 'community':
      return 'Community';
    default:
      return 'Token';
  }
}
